import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CompanyOnboardingStepKey } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';

import { hasCompanyOnboardingRecords } from '~queries/companyOnboardingRecords';
import { useStores } from '~store';

import CompanyOnboardingCsvImportZone from './CompanyOnboardingCsvImportZone';
import { OnboardingCard, SecondaryText, TitleText } from './CompanyOnboardingPrimitives';
import { companyOnboardingSteps } from './companyOnboardingSteps';
import CompanyOnboardingRecordsPanel from './records/CompanyOnboardingRecordsPanel';

interface CompanyOnboardingStepContentProps {
  companyId: string;
  stepKey: CompanyOnboardingStepKey;
}

const CompanyOnboardingStepContent = observer(
  ({ companyId, stepKey }: CompanyOnboardingStepContentProps) => {
    const { userStore } = useStores();
    const {
      WorkArea,
      afterWorkArea,
      beforeWorkArea,
      canAccess,
      conceptCardFooters,
      conceptCards = [],
      csvConfigKey,
      settingsLink,
    } = companyOnboardingSteps[stepKey];

    if (!canAccess(userStore)) {
      return <SecondaryText>{t('company_onboarding.no_access')}</SecondaryText>;
    }

    return (
      <Box>
        {conceptCards.length ? (
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {conceptCards.map((cardKey) => (
              <OnboardingCard
                data-test-id={`company-onboarding-concept-${cardKey}`}
                key={cardKey}
                sx={{ flex: 1, p: 2 }}
              >
                <TitleText>
                  {t(`company_onboarding.concept_cards.${cardKey}.title`)}
                </TitleText>
                <SecondaryText sx={{ mt: 0.5 }}>
                  {t(`company_onboarding.concept_cards.${cardKey}.aliases`)}
                </SecondaryText>
                <Typography sx={{ mt: 1 }}>
                  {t(`company_onboarding.concept_cards.${cardKey}.body`)}
                </Typography>
                {conceptCardFooters?.[cardKey]}
              </OnboardingCard>
            ))}
          </Box>
        ) : null}
        {beforeWorkArea}
        {csvConfigKey ? (
          <CompanyOnboardingCsvImportZone
            companyId={companyId}
            configKey={csvConfigKey}
            entityLabel={t(`company_onboarding.modules.${stepKey}.list_label`)}
            stepKey={stepKey}
          />
        ) : null}
        {WorkArea ? (
          <WorkArea companyId={companyId} settingsLink={settingsLink} stepKey={stepKey} />
        ) : null}
        {afterWorkArea}
        {hasCompanyOnboardingRecords(stepKey) ? (
          <CompanyOnboardingRecordsPanel
            {...companyOnboardingSteps[stepKey].records}
            companyId={companyId}
            settingsLink={settingsLink}
            stepKey={stepKey}
          />
        ) : null}
      </Box>
    );
  },
);

export default CompanyOnboardingStepContent;
